/**
 * Item Detail Drawer
 * Basic info (read-only) + 5 Tabs: Barcode, Logistics, Sales, Storage (all read-only), Weight Threshold (editable)
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Button,
  Spin,
  Form,
  InputNumber,
  Row,
  Col,
  message,
  Divider,
  Descriptions,
  Tag,
  Image,
} from 'antd';
import { useTranslation } from 'react-i18next';
import type { ItemDetailDTO } from '../../../../types/master-data/item';
import * as itemService from '../../../../services/master-data/itemService';
import { ItemStatusOptions } from '../../../../constants/masterData';

interface ItemDetailProps {
  visible: boolean;
  code: string;
  ownerCode: string;
  onClose: () => void;
  onSave?: () => void;
}

const ItemDetail: React.FC<ItemDetailProps> = ({ visible, code, ownerCode, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<ItemDetailDTO | null>(null);

  useEffect(() => {
    if (visible && code) {
      loadDetail();
    }
  }, [visible, code]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await itemService.getItemByCode(ownerCode, code);
      if (response.success && response.data) {
        const d = response.data;
        setDetail(d);
        form.setFieldsValue({
          weightUpperLimit: d.weightUpperLimit ?? 120,
          weightLowerLimit: d.weightLowerLimit ?? 50,
        });
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const params = {
        code,
        ownerCode,
        weightUpperLimit: values.weightUpperLimit,
        weightLowerLimit: values.weightLowerLimit,
      };
      const response = await itemService.updateWeightLimit(params);
      if (response.success) {
        message.success(t('masterData.item.detail.saveSuccess'));
        onSave?.();
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch {
      // form validation failed
    } finally {
      setSaving(false);
    }
  };

  const renderStatusTag = (status: string) => {
    const option = ItemStatusOptions.find((opt) => opt.value === status);
    if (!option) return status || '-';
    return <Tag color={option.color}>{option.label}</Tag>;
  };

  const displayValue = (val: any) => {
    if (val === null || val === undefined || val === '') return '-';
    return String(val);
  };

  const tabItems = [
    {
      key: 'barcode',
      label: t('masterData.item.detail.barcode'),
      forceRender: true,
      children: (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.barcode')}>{displayValue(detail?.barcode)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'sales',
      label: t('masterData.item.detail.salesAttribute'),
      forceRender: true,
      children: (
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.priceGF')}>{detail?.priceGf != null ? detail.priceGf.toFixed(3) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.priceHY')}>{detail?.priceHy != null ? detail.priceHy.toFixed(3) : '-'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'weight',
      label: t('masterData.item.detail.weightThreshold'),
      forceRender: true,
      children: (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="weightUpperLimit"
                label={t('masterData.item.detail.upperLimit')}
                rules={[{
                  validator: (_, value) => {
                    if (value === null || value === undefined) return Promise.resolve();
                    if (value < 1 || value > 500) return Promise.reject(t('common.validation.weightUpperLimitRange'));
                    return Promise.resolve();
                  }
                }]}
              >
                <InputNumber
                  precision={0}
                  style={{ width: '100%' }}
                  placeholder={t('masterData.item.detail.upperLimitPlaceholder')}
                  onBlur={(e) => {
                    const val = Number(e.target.value);
                    if (!isNaN(val)) {
                      const clamped = Math.max(1, Math.min(500, val));
                      form.setFieldsValue({ weightUpperLimit: clamped });
                      form.validateFields(['weightUpperLimit']);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="weightLowerLimit"
                label={t('masterData.item.detail.lowerLimit')}
                rules={[{
                  validator: (_, value) => {
                    if (value === null || value === undefined) return Promise.resolve();
                    if (value < 1 || value > 99) return Promise.reject(t('common.validation.weightLowerLimitRange'));
                    return Promise.resolve();
                  }
                }]}
              >
                <InputNumber
                  precision={0}
                  style={{ width: '100%' }}
                  placeholder={t('masterData.item.detail.lowerLimitPlaceholder')}
                  onBlur={(e) => {
                    const val = Number(e.target.value);
                    if (!isNaN(val)) {
                      const clamped = Math.max(1, Math.min(99, val));
                      form.setFieldsValue({ weightLowerLimit: clamped });
                      form.validateFields(['weightLowerLimit']);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ),
    },
  ];

  return (
    <Drawer
      title={`${t('masterData.item.detail.title')} - ${code}`}
      placement="right"
      width={960}
      onClose={onClose}
      open={visible}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>{t('masterData.item.detail.cancel')}</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>{t('masterData.item.detail.save')}</Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Basic Information - Read Only */}
        <Divider orientation="left" style={{ marginTop: 0 }}>{t('masterData.item.detail.basicInfo')}</Divider>
        <Descriptions column={3} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.itemCode')}>{displayValue(detail?.code)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.ownerCode')}>{displayValue(detail?.ownerCode)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.itemName')}>{displayValue(detail?.name)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.englishName')}>{displayValue(detail?.englishName)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.status')}>{detail?.status ? renderStatusTag(detail.status) : '-'}</Descriptions.Item>
        </Descriptions>

        {/* Dimension & Weight - Read Only */}
        <Divider orientation="left">{t('masterData.item.detail.dimensionWeight')}</Divider>
        <Descriptions column={4} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.length')}>{displayValue(detail?.length)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.width')}>{displayValue(detail?.width)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.height')}>{displayValue(detail?.height)}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.weight')}>{displayValue(detail?.weight)}</Descriptions.Item>
        </Descriptions>

        {/* Category & Brand - Read Only */}
        <Divider orientation="left">{t('masterData.item.detail.categoryBrand')}</Divider>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.categoryGF')}>
            {displayValue(
              [detail?.catGfLv1Name, detail?.catGfLv2Name, detail?.catGfLv3Name]
                .filter(Boolean)
                .join(' > ') || null
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.categoryHY')}>
            {displayValue(
              [detail?.catHyLv1Name, detail?.catHyLv2Name, detail?.catHyLv3Name]
                .filter(Boolean)
                .join(' > ') || null
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.brand')}>{displayValue(detail?.brandNameEn || detail?.brandNameTh)}</Descriptions.Item>
        </Descriptions>

        {/* Other Information - Read Only */}
        <Divider orientation="left">{t('masterData.item.detail.otherInfo')}</Divider>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('masterData.item.detail.priceGF')}>{detail?.priceGf != null ? `฿ ${detail.priceGf.toFixed(3)}` : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.priceHY')}>{detail?.priceHy != null ? `฿ ${detail.priceHy.toFixed(3)}` : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('masterData.item.detail.image')} span={2}>
            {(() => {
              if (!detail?.image) return '-';
              try {
                const urls: string[] = JSON.parse(detail.image);
                if (!urls.length) return '-';
                return (
                  <Image.PreviewGroup>
                    {urls.map((url, i) => (
                      <Image key={i} src={url} width={80} height={80} style={{ objectFit: 'cover', marginRight: 8 }} />
                    ))}
                  </Image.PreviewGroup>
                );
              } catch {
                return <Image src={detail.image} width={80} height={80} style={{ objectFit: 'cover' }} />;
              }
            })()}
          </Descriptions.Item>
        </Descriptions>

        {/* Tab Area */}
        <div style={{ marginTop: 16 }}>
          <Tabs items={tabItems} />
        </div>
      </Spin>
    </Drawer>
  );
};

export default ItemDetail;
